import is from '@sindresorhus/is';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import type { LanguageCodeForJSA } from '~/shared/internationalization';

import { URL_TILE_SEARCH_PARAM } from '../../url-tiles';
import { createJsaProfileDetailTilePayload } from '../url-tile-payloads';
import { jsaProfileDetailUrlTileDefinition } from '../url-tiles/tile-definitions';

export type StandaloneProfileItemType = 'job-level' | 'job-role';

export type NavigateToStandaloneProfileParams = {
    readonly architectureId: string;
    readonly isCommentsOpen?: boolean;
    readonly itemId: string;
    readonly itemType: StandaloneProfileItemType;
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly version?: number;
};

type UseNavigateToStandaloneProfileReturn = {
    readonly getProfilePath: (params: NavigateToStandaloneProfileParams) => string;
    readonly navigate: (params: NavigateToStandaloneProfileParams) => void;
};

export const parseProfileVersion = (selectedVersion: string | undefined): number | undefined => {
    if (!is.nonEmptyString(selectedVersion)) {
        return undefined;
    }

    const version = Number.parseInt(selectedVersion, 10);

    return Number.isNaN(version) ? undefined : version;
};

const getLegacyProfilePath = ({ architectureId, itemId, itemType }: NavigateToStandaloneProfileParams): string =>
    `/job-profile/${architectureId}/${itemType}/${itemId}`;

export const serializeProfileDetailToken = ({
    architectureId,
    isCommentsOpen,
    itemId,
    itemType,
    preferredLanguage,
    version,
}: NavigateToStandaloneProfileParams & { readonly version: number }): string | undefined =>
    jsaProfileDetailUrlTileDefinition.serializePayload(
        createJsaProfileDetailTilePayload({
            architectureId,
            isCommentsOpen: isCommentsOpen ? true : undefined,
            itemType,
            mode: 'view',
            preferredLanguage,
            profileId: itemId,
            version,
        }),
    );

export const getProfilePath = (params: NavigateToStandaloneProfileParams): string => {
    const { version } = params;

    if (is.undefined(version)) {
        return getLegacyProfilePath(params);
    }

    const token = serializeProfileDetailToken({ ...params, version });

    if (is.undefined(token)) {
        return getLegacyProfilePath(params);
    }

    const searchParams = new URLSearchParams();
    searchParams.append(URL_TILE_SEARCH_PARAM, token);

    return `/job-profile?${searchParams.toString()}`;
};

export const useNavigateToStandaloneProfile = (): UseNavigateToStandaloneProfileReturn => {
    const routerNavigate = useNavigate();

    const navigate = useCallback(
        (params: NavigateToStandaloneProfileParams): void => {
            routerNavigate(getProfilePath(params));
        },
        [routerNavigate],
    );

    return { getProfilePath, navigate };
};
